<template>
  <div class="asnip">
    <p class="asnip__lead">
      몰·포털·판매 화면에 노출되는 안내 문구를 키 단위로 수정합니다. 저장 즉시 공개 API에 반영됩니다.
      당일 출고 안내는 <code>mall.product.same_day_cutoff</code> 이며, 본문을 비우면 상품상세·스토어에서
      숨겨집니다.
    </p>

    <section class="sui-card sui-card--flush" data-tour="admin-content-snippets-list">
      <div class="sui-toolbar" data-tour="admin-content-snippets-toolbar">
        <strong>멘트 목록</strong>
        <label class="asnip__filter">
          <span>그룹</span>
          <select v-model="group" @change="load">
            <option value="">전체</option>
            <option value="mall">몰</option>
            <option value="portal">포털</option>
            <option value="seller">판매</option>
          </select>
        </label>
        <span class="sui-toolbar__spacer" />
        <button type="button" class="sui-btn" :disabled="loading" @click="load">새로고침</button>
      </div>

      <p v-if="errorMessage" class="asnip__error">
        <AppIcon icon="mdi-alert-outline" :size="16" />
        {{ errorMessage }}
      </p>

      <div v-if="snippets.length" class="asnip__grid">
        <article
          v-for="row in snippets"
          :key="row.key"
          class="asnip__card"
          data-tour="admin-content-snippets-item"
        >
          <header>
            <div>
              <code>{{ row.key }}</code>
              <h3>{{ row.title }}</h3>
            </div>
            <span class="sui-badge sui-badge--gray">{{ row.group }} · {{ row.format }}</span>
          </header>
          <p class="asnip__preview">{{ previewText(row.body) }}</p>
          <footer>
            <span v-if="row.updatedAt" class="asnip__meta">수정 {{ row.updatedAt }}</span>
            <button type="button" class="sui-btn sui-btn--sm" @click="openEdit(row)">편집</button>
          </footer>
        </article>
      </div>
      <p v-else-if="!loading" class="asnip__empty">등록된 멘트가 없습니다.</p>
    </section>

    <div v-if="modalOpen" class="asnip__modal" role="dialog" aria-modal="true">
      <div class="asnip__modal-backdrop" @click="closeModal" />
      <div class="asnip__modal-panel" data-tour="admin-content-snippets-editor">
        <header>
          <h2>멘트 편집</h2>
          <button type="button" class="sui-btn sui-btn--sm" @click="closeModal">닫기</button>
        </header>

        <div class="asnip__form">
          <label>
            <span>키</span>
            <input :value="form.key" readonly />
          </label>
          <label>
            <span>그룹</span>
            <select v-model="form.group">
              <option value="mall">mall</option>
              <option value="portal">portal</option>
              <option value="seller">seller</option>
            </select>
          </label>
          <label>
            <span>형식</span>
            <select v-model="form.format">
              <option value="text">text</option>
              <option value="html">html</option>
            </select>
          </label>
          <label class="is-full">
            <span>제목</span>
            <input v-model.trim="form.title" maxlength="120" />
          </label>
          <label class="is-full">
            <span>본문</span>
            <textarea v-model="form.body" rows="12" />
          </label>
          <p v-if="form.key === 'mall.product.same_day_cutoff'" class="asnip__field-hint">
            <code>{cutoff}</code> 자리에 판매업체 마감 시각(예: 14시)이 들어갑니다. 본문을 비우면
            상품상세·스토어에서 당일 출고 안내가 숨겨집니다.
          </p>
        </div>

        <div class="asnip__live" data-tour="admin-content-snippets-preview">
          <h3>미리보기</h3>
          <div v-if="form.format === 'html'" class="asnip__html" v-html="form.body" />
          <p v-else class="asnip__text">{{ form.body }}</p>
        </div>

        <footer>
          <button type="button" class="sui-btn" @click="closeModal">취소</button>
          <button type="button" class="sui-btn sui-btn--primary" :disabled="saving" @click="onSave">
            {{ saving ? '저장 중…' : '저장' }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  fetchAdminSnippets,
  saveAdminSnippet,
  type ContentSnippet,
} from '@/api/admin'

const snippets = ref<ContentSnippet[]>([])
const group = ref('')
const loading = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const modalOpen = ref(false)

const form = reactive<ContentSnippet>({
  key: '',
  title: '',
  group: 'mall',
  format: 'text',
  body: '',
})

function previewText(body: string) {
  return body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    snippets.value = await fetchAdminSnippets(group.value)
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '멘트를 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

function openEdit(row: ContentSnippet) {
  form.key = row.key
  form.title = row.title
  form.group = row.group
  form.format = row.format
  form.body = row.body
  modalOpen.value = true
}

function closeModal() {
  modalOpen.value = false
}

async function onSave() {
  if (!form.key || !form.title) {
    window.alert('제목을 입력해 주세요.')
    return
  }
  saving.value = true
  try {
    await saveAdminSnippet({ ...form })
    modalOpen.value = false
    await load()
  } catch (error) {
    window.alert(error instanceof Error ? error.message : '저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

onMounted(load)
</script>

<style lang="scss" src="./AdminContentSnippetsPage.scss"></style>
